(() => {
  const MAX_ITEMS = 3;
  const STORAGE_KEY = 'tt-product-compare-v11';
  const selected = new Map();

  let root = null;
  let tray = null;
  let slotsEl = null;
  let openBtn = null;
  let countEl = null;
  let modal = null;
  let modalBody = null;
  let dialog = null;
  let lastFocus = null;
  let bound = false;

  function qs(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function qsa(sel, scope) {
    return Array.from((scope || document).querySelectorAll(sel));
  }

  let catalog = {};

  function loadCatalog() {
    const node = qs('[data-tt-compare-catalog]');
    if (!node) return;
    try {
      catalog = JSON.parse(node.textContent) || {};
    } catch (e) {
      catalog = {};
    }
  }

  function decodeText(value) {
    return String(value || '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
  }

  function isDimensionLine(text) {
    return /dimension|d\s*x\s*w\s*x\s*h|l\s*x\s*w\s*x\s*h|fully reclined|seat height|seat depth|seat length|arm height|bed leg|product weight|"w\s*x|"d\s*x|\d[\d.]*\s*"\s*[wdh]/i.test(String(text || ''));
  }

  function uniqueLines(lines) {
    const seen = new Set();
    return (lines || []).filter(function (line) {
      const key = String(line || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function optionValues(opt) {
    return ((opt && opt.values) || [])
      .map(function (value) {
        return typeof value === 'string' ? value : value && (value.name || value.label || '');
      })
      .filter(Boolean);
  }

  function optionKind(name) {
    const key = String(name || '').toLowerCase();
    if (/fabric|cover|material/.test(key)) return 'fabric';
    if (/color|colour/.test(key)) return 'color';
    if (/size|dimension/.test(key)) return 'size';
    return 'other';
  }

  function isFabricValue(text) {
    return /breathing\s*leather|chenille|linen|velvet|microfiber|suede|faux\s*leather|pu\s*leather|\bleather\b|\bfabric\b/i.test(
      String(text || '')
    );
  }

  function isSizeHeading(text) {
    return /^(twin(\s*xl)?|full|queen|king|california\s*king|cal(\.|\s*)?king|split\s*king|split\s*california\s*king|medium|oversize|petite|standard)$/i.test(
      String(text || '')
        .replace(/\s+/g, ' ')
        .trim()
    );
  }

  function parseSizeGroups(lines) {
    const groups = [];
    const leftover = [];
    let current = null;
    (lines || []).forEach(function (line) {
      const text = String(line || '').trim();
      if (!text) return;
      if (isSizeHeading(text)) {
        current = { name: text, specs: [] };
        groups.push(current);
        return;
      }
      if (current) current.specs.push(text);
      else leftover.push(text);
    });
    return { groups: groups, leftover: leftover };
  }

  function productCode(item) {
    const raw = [item && item.suffix, item && item.title, item && item.url, item && item.handle]
      .map(function (value) {
        return String(value || '');
      })
      .join(' ');
    const hit = raw.match(/6010|6011|6012|6013|6015|t600|t500|t450|f50/i);
    return hit ? hit[0].toLowerCase() : '';
  }

  function isLiftChair(item) {
    return /6010|6011|6012|6013|6015/.test(productCode(item) || String((item && item.suffix) || ''));
  }

  function catalogCode(suffix) {
    const raw = String(suffix || '');
    const hit = raw.match(/6010|6011|6012|6013|6015|t600|t500|t450|f50/i);
    return hit ? hit[0].toLowerCase() : raw.toLowerCase();
  }

  function catalogEntry(product) {
    const code = productCode(product) || catalogCode((product && product.suffix) || '');
    return catalog[code] || catalog[String((product && product.suffix) || '').toLowerCase()] || {};
  }

  function isLegacyFeatureCopy(text) {
    return /independent dual-motor|power lift function helps|175\s*°?\s*max recline|8-point vibration|lumbar heat provides|charging ports keep|convenient side pockets|dual-side anti-fall|built-in cup holders keep|extended footrest provides|adjustable headrest provides|perfect for petite|sleek and sophisticated|ultra-plush back|powered and within easy reach|making relaxing moments|adjust the backrest|the power lift|gently elevate|soft lumbar warmth|choose from multiple|plush cushioning/i.test(
      String(text || '')
    );
  }

  function isSellingPointTitle(text) {
    const raw = compactText(text);
    if (!raw) return false;
    if (isLegacyFeatureCopy(raw)) return false;
    if (raw.length > 32) return false;
    if (/[.。]/.test(raw)) return false;
    if (raw.split(' ').length > 4) return false;
    if (/\b(allows|helps|provides|making|keep your|offering|designed|ideal for)\b/i.test(raw)) return false;
    return true;
  }

  function liftChairFeatureTitles() {
    return Array.isArray(catalog.liftChairFeatures) ? catalog.liftChairFeatures.map(decodeText) : [];
  }

  function enrichProduct(product) {
    const suffix = String(product.suffix || '').toLowerCase();
    const extra = catalogEntry(product);
    const chair = isLiftChair(product);
    let highlights = [];
    if (chair) {
      highlights = (Array.isArray(extra.highlights) ? extra.highlights : [])
        .map(decodeText)
        .filter(isSellingPointTitle);
      if (!highlights.length) highlights = liftChairFeatureTitles().filter(isSellingPointTitle);
    } else if (Array.isArray(extra.highlights) && extra.highlights.length) {
      highlights = extra.highlights.map(decodeText).filter(isSellingPointTitle);
    }
    const specDetails = Array.isArray(extra.details) ? extra.details.map(decodeText) : [];
    const colors = [];
    const fabrics = [];
    const sizes = [];
    const leftoverOptions = [];
    (Array.isArray(product.options) ? product.options : []).forEach(function (opt) {
      if (!opt || !opt.name) return;
      const values = optionValues(opt);
      if (!values.length) return;
      const kind = optionKind(opt.name);
      if (kind === 'fabric') {
        fabrics.push.apply(fabrics, values);
        return;
      }
      if (kind === 'color') {
        values.forEach(function (value) {
          if (isFabricValue(value)) fabrics.push(value);
          else colors.push(value);
        });
        return;
      }
      if (kind === 'size') sizes.push.apply(sizes, values);
      else leftoverOptions.push(opt.name + ': ' + values.join(', '));
    });
    const typeDetails = [];
    if (product.type) typeDetails.push('Type: ' + product.type);
    if (product.vendor) typeDetails.push('Brand: ' + product.vendor);

    const parsed = parseSizeGroups(specDetails);
    const sizeGroups = parsed.groups;
    const specSource = sizeGroups.length ? parsed.leftover : specDetails;
    const leftoverSpecs = specSource.filter((line) => !isDimensionLine(line));

    product.highlights = highlights;
    product.colorSwatches = Array.isArray(product.colorSwatches) ? product.colorSwatches : [];
    product.colors = uniqueLines(
      product.colorSwatches.length
        ? product.colorSwatches.map(function (swatch) {
            return swatch && swatch.name;
          })
        : colors
    );
    product.fabrics = uniqueLines(
      Array.isArray(extra.fabrics) && extra.fabrics.length ? extra.fabrics.map(decodeText) : fabrics
    );
    product.sizes = uniqueLines(
      sizes.concat(
        sizeGroups.map(function (group) {
          return group.name;
        })
      )
    );
    product.sizeGroups = sizeGroups;
    if (chair) {
      product.description = '';
      product.specLines = specSource;
      product.dimensions = [];
      product.details = [];
    } else {
      product.specLines = [];
      product.dimensions = uniqueLines(specSource.filter(isDimensionLine));
      product.details = typeDetails.concat(leftoverOptions, leftoverSpecs);
    }
    return product;
  }

  function pageCollection() {
    const node = qs('[data-tt-compare-collection]');
    const fromNode = node && node.getAttribute('data-tt-compare-collection');
    if (fromNode) return String(fromNode).toLowerCase();
    const match = String(window.location.pathname || '').match(/\/collections\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]).toLowerCase() : '';
  }

  function itemCollection(item) {
    return String((item && item.collection) || '').toLowerCase();
  }

  function keepForCurrentCollection(item) {
    const page = pageCollection();
    const col = itemCollection(item);
    return Boolean(page && col && page === col);
  }

  function readProductFromButton(btn) {
    const cardRoot = btn.closest('.tt-bed-card, .grid__item, .product-card-wrapper, .card');
    const jsonNode = cardRoot ? cardRoot.querySelector('[data-tt-compare-json]') : null;
    let data = {
      id: String(btn.dataset.productId || ''),
      title: btn.dataset.productTitle || '',
      url: btn.dataset.productUrl || '',
      image: btn.dataset.productImage || '',
      price: btn.dataset.productPrice || '',
      comparePrice: btn.dataset.productComparePrice || '',
      description: btn.dataset.productDescription || '',
      suffix: '',
      type: '',
      vendor: '',
      options: [],
    };
    if (jsonNode) {
      try {
        data = Object.assign(data, JSON.parse(jsonNode.textContent));
        data.id = String(data.id || btn.dataset.productId || '');
      } catch (e) {
        /* keep dataset fallback */
      }
    }
    data.collection = String(data.collection || pageCollection() || '').toLowerCase();
    return enrichProduct(data);
  }

  function persist() {
    try {
      const page = pageCollection();
      let others = [];
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const list = JSON.parse(raw);
        if (Array.isArray(list)) {
          others = list.filter(function (item) {
            return item && item.id && itemCollection(item) && itemCollection(item) !== page;
          });
        }
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(others.concat([...selected.values()])));
    } catch (e) {
      /* ignore */
    }
  }

  function restore() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return;
      list.forEach(function (item) {
        if (item && item.id && keepForCurrentCollection(item)) {
          selected.set(String(item.id), item);
        }
      });
      [...selected.keys()].slice(MAX_ITEMS).forEach(function (id) {
        selected.delete(id);
      });
      selected.forEach(function (item, id) {
        const prevColor = item && item.selectedColor;
        const prevCollection = itemCollection(item);
        const btn = qs('[data-tt-compare-toggle][data-product-id="' + String(id) + '"]');
        const next = btn ? readProductFromButton(btn) : enrichProduct(item);
        if (prevColor) next.selectedColor = prevColor;
        if (!itemCollection(next) && prevCollection) next.collection = prevCollection;
        selected.set(id, next);
      });
      selected.forEach(function (item, id) {
        if (!keepForCurrentCollection(item)) selected.delete(id);
      });
      persist();
    } catch (e) {
      /* ignore */
    }
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/`/g, '&#96;');
  }

  function syncButtons() {
    qsa('[data-tt-compare-toggle]').forEach((btn) => {
      const id = String(btn.dataset.productId || '');
      const on = selected.has(id);
      const pressed = on ? 'true' : 'false';
      const nextLabel = on ? 'Selected' : 'Compare';
      btn.classList.toggle('is-selected', on);
      if (btn.getAttribute('aria-pressed') !== pressed) {
        btn.setAttribute('aria-pressed', pressed);
      }
      const label = btn.querySelector('[data-tt-compare-label]');
      if (label && label.textContent !== nextLabel) label.textContent = nextLabel;
    });
  }

  function renderSlots() {
    if (!slotsEl) return;
    const items = [...selected.values()];
    let html = '';
    for (let i = 0; i < MAX_ITEMS; i += 1) {
      const item = items[i];
      if (item) {
        html +=
          '<div class="tt-compare-tray__slot is-filled" data-id="' +
          escapeAttr(item.id) +
          '">' +
          '<button type="button" class="tt-compare-tray__remove" data-tt-compare-remove="' +
          escapeAttr(item.id) +
          '" aria-label="Remove ' +
          escapeAttr(item.title) +
          '">×</button>' +
          (displayImage(item)
            ? '<img class="tt-compare-tray__thumb" src="' +
              escapeAttr(displayImage(item)) +
              '" alt="" width="72" height="72" loading="lazy">'
            : '<div class="tt-compare-tray__thumb tt-compare-tray__thumb--empty" aria-hidden="true"></div>') +
          '<p class="tt-compare-tray__name">' +
          escapeHtml(item.title) +
          '</p></div>';
      } else {
        html +=
          '<div class="tt-compare-tray__slot is-empty" aria-hidden="true">' +
          '<div class="tt-compare-tray__placeholder">Add product</div></div>';
      }
    }
    slotsEl.innerHTML = html;
  }

  function updateTray() {
    ensureEls();
    if (!tray || !openBtn || !countEl) return;
    const count = selected.size;
    countEl.textContent = '(' + count + ')';
    openBtn.disabled = count < 2;
    if (count > 0) {
      tray.hidden = false;
      tray.classList.add('is-open');
      document.documentElement.classList.add('tt-compare-tray-open');
    } else {
      tray.hidden = true;
      tray.classList.remove('is-open');
      document.documentElement.classList.remove('tt-compare-tray-open');
    }
    renderSlots();
    syncButtons();
    persist();
  }

  function toggleProduct(btn) {
    const product = readProductFromButton(btn);
    if (!product.id) return;
    if (!keepForCurrentCollection(product)) return;

    if (selected.has(product.id)) {
      selected.delete(product.id);
      updateTray();
      return;
    }

    selected.forEach(function (item, id) {
      if (!keepForCurrentCollection(item)) selected.delete(id);
    });

    if (selected.size >= MAX_ITEMS) {
      openLimitNotice();
      return;
    }

    selected.set(product.id, product);
    updateTray();
  }

  function removeProduct(id) {
    selected.delete(String(id));
    updateTray();
    if (modal && !modal.hidden) renderModal();
  }

  function clearAll() {
    selected.clear();
    closeModal();
    updateTray();
  }

  function listHtml(items) {
    if (!items || !items.length) {
      return '<p class="tt-compare-modal__empty-cell">—</p>';
    }
    return (
      '<ul class="tt-compare-modal__list">' +
      items.map((item) => '<li>' + escapeHtml(decodeText(item)) + '</li>').join('') +
      '</ul>'
    );
  }

  function compactText(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  function specId(text) {
    return compactText(text)
      .toLowerCase()
      .replace(/[^a-z0-9&+]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  const SPEC_ALIASES = [
    { match: /adjustable legs?|bed legs?|leg height/i, id: 'adjustable-legs', key: 'Adjustable legs' },
    { match: /item dimensions|dimensions l x w x h|dimensions d x w x h/i, id: 'item-dimensions', key: 'Item Dimensions' },
    { match: /maximum weight|weight recommendation|weight capacity|weight limits/i, id: 'max-weight', key: 'Maximum Weight Recommendation' },
    { match: /operating noise|quiet motor/i, id: 'operating-noise', key: 'Operating Noise' },
    { match: /head adjustment/i, id: 'head-angle', key: 'Head Adjustment Angle' },
    { match: /foot adjustment/i, id: 'foot-angle', key: 'Foot Adjustment Angle' },
    { match: /product weight/i, id: 'product-weight', key: 'Product Weight' },
    { match: /fully reclined/i, id: 'fully-reclined', key: 'Fully Reclined Length' },
    { match: /seat height/i, id: 'seat-height', key: 'Seat Height' },
    { match: /seat depth/i, id: 'seat-depth', key: 'Seat Depth' },
    { match: /seat length/i, id: 'seat-length', key: 'Seat Length' },
    { match: /arm height/i, id: 'arm-height', key: 'Arm Height' },
  ];

  function canonicalizeSpec(spec) {
    if (!spec) return null;
    const hay = spec.key + ' ' + spec.id;
    for (let i = 0; i < SPEC_ALIASES.length; i += 1) {
      if (SPEC_ALIASES[i].match.test(hay)) {
        return { id: SPEC_ALIASES[i].id, key: SPEC_ALIASES[i].key, value: spec.value };
      }
    }
    return spec;
  }

  function parseSpecLine(text) {
    const raw = compactText(decodeText(String(text || '').replace(/<[^>]+>/g, ' ')));
    if (!raw || /^(type|brand)\s*:/i.test(raw)) return null;
    if (isLegacyFeatureCopy(raw)) return null;
    if (/perfect for|sleek and|ultra-plush|transitional style|allows the|helps users/i.test(raw)) return null;
    if (/^\d/.test(raw) && /[wdh]/i.test(raw) && !/dimension|weight|height|depth|length/i.test(raw)) {
      return null;
    }

    const labeled = [
      { match: /^\s*(Maximum Weight Recommendation)\s*[:：]?\s*(.+)$/i, key: 'Maximum Weight Recommendation' },
      { match: /^\s*(Item Dimensions(?:\s+[DL]\s*x\s*W\s*x\s*H)?(?:\s*\(in\.\))?)\s*[:：]?\s*(.+)$/i, key: 'Item Dimensions' },
      { match: /^\s*(Fully Reclined Length)\s*[:：]?\s*(.+)$/i, key: 'Fully Reclined Length' },
      { match: /^\s*(Product Weight)\s*[:：]?\s*(.+)$/i, key: 'Product Weight' },
      { match: /^\s*(Seat Height)\s*[:：]?\s*(.+)$/i, key: 'Seat Height' },
      { match: /^\s*(Seat Depth)\s*[:：]?\s*(.+)$/i, key: 'Seat Depth' },
      { match: /^\s*(Seat Length)\s*[:：]?\s*(.+)$/i, key: 'Seat Length' },
      { match: /^\s*(Arm Height)\s*[:：]?\s*(.+)$/i, key: 'Arm Height' },
      { match: /^\s*(Head Adjustment Angle)\s*[:：]?\s*(.+)$/i, key: 'Head Adjustment Angle' },
      { match: /^\s*(Foot Adjustment Angle)\s*[:：]?\s*(.+)$/i, key: 'Foot Adjustment Angle' },
      { match: /^\s*(Operating Noise)\s*[:：]?\s*(.+)$/i, key: 'Operating Noise' },
      { match: /^\s*(Adjustable legs?|Bed legs?)\s*[:：]?\s*(.+)$/i, key: 'Adjustable legs' },
    ];
    for (let i = 0; i < labeled.length; i += 1) {
      const hit = raw.match(labeled[i].match);
      if (hit && hit[2] && hit[2].trim()) {
        return canonicalizeSpec({
          id: specId(labeled[i].key),
          key: labeled[i].key,
          value: hit[2].trim(),
        });
      }
    }

    if (/[:：]/.test(raw)) {
      const idx = raw.search(/[:：]/);
      const key = raw.slice(0, idx).trim();
      const value = raw.slice(idx + 1).trim();
      if (key && value) return canonicalizeSpec({ id: specId(key), key: key, value: value });
    }

    const numbered = raw.match(/^(.*?)(?:\s+)(\d[\d.,/"'″’“”*\s°a-z]*)$/i);
    if (
      numbered &&
      numbered[1].trim().length > 2 &&
      /dimension|weight|height|depth|length|noise|angle|leg|capacity|recommendation|reclined/i.test(numbered[1])
    ) {
      return canonicalizeSpec({
        id: specId(numbered[1]),
        key: numbered[1].replace(/[:：]\s*$/, '').trim(),
        value: numbered[2].trim(),
      });
    }

    const ineq = raw.match(/^(.*?)\s*(≤|>=?)\s*(.+)$/);
    if (ineq && ineq[1].trim()) {
      return canonicalizeSpec({
        id: specId(ineq[1]),
        key: ineq[1].trim(),
        value: (ineq[2] + ' ' + ineq[3]).trim(),
      });
    }

    return null;
  }

  function featureParts(text) {
    const raw = compactText(decodeText(text));
    const paren = raw.match(/^(.*?)\s*\((.*)\)\s*$/);
    if (paren && paren[1].trim()) {
      return { id: specId(paren[1]), key: compactText(paren[1]), extra: compactText(paren[2]) };
    }
    return { id: specId(raw), key: raw, extra: '' };
  }

  function finalizeMatrixRow(label, values) {
    const filled = values.map(function (value) {
      if (value === true) return 'HAS';
      if (value == null || value === '') return '';
      return String(value);
    });
    const present = filled.filter(Boolean);
    if (!present.length) return null;

    const unique = [];
    present.forEach(function (value) {
      if (unique.indexOf(value) === -1) unique.push(value);
    });

    const allHave = present.length === values.length;
    if (unique.length === 1 && (unique[0] === 'HAS' || allHave)) {
      return {
        label: label,
        cells: filled.map(function (value) {
          return value ? 'check' : 'empty';
        }),
      };
    }

    return {
      label: label,
      cells: filled.map(function (value) {
        if (!value) return 'empty';
        if (value === 'HAS') return 'check';
        return value;
      }),
    };
  }

  function collapseSpecParts(parts) {
    const groups = [];
    const byValue = {};
    (parts || []).forEach(function (part) {
      const value = part.value === true ? 'HAS' : String(part.value || '');
      if (!value) return;
      if (!byValue[value]) {
        byValue[value] = { value: value, sizes: [] };
        groups.push(byValue[value]);
      }
      if (part.size && byValue[value].sizes.indexOf(part.size) === -1) {
        byValue[value].sizes.push(part.size);
      }
    });
    return groups;
  }

  function specCellFromParts(parts) {
    const groups = collapseSpecParts(parts).filter(function (group) {
      return group.value && group.value !== 'HAS' && group.value !== 'check';
    });
    if (!groups.length) return 'empty';
    return { sized: groups };
  }

  function cellCompareKey(cell) {
    if (cell === 'check' || cell === 'empty' || cell == null || cell === '') return String(cell || 'empty');
    if (cell && cell.sized) {
      return cell.sized
        .map(function (group) {
          return group.value;
        })
        .join('|');
    }
    return String(cell);
  }

  function finalizeSpecRow(label, cells) {
    const keys = cells.map(cellCompareKey);
    const present = keys.filter(function (key) {
      return key && key !== 'empty' && key !== 'check' && key !== 'HAS';
    });
    if (!present.length) return null;

    return {
      label: label,
      cells: cells.map(function (cell) {
        const key = cellCompareKey(cell);
        if (!key || key === 'empty' || key === 'check' || key === 'HAS') return 'empty';
        return cell;
      }),
    };
  }

  function collectSpecMap(item) {
    const map = {};
    function add(spec, size) {
      if (!spec || spec.value === true || spec.value === 'HAS') return;
      if (!map[spec.id]) map[spec.id] = { id: spec.id, key: spec.key, parts: [] };
      map[spec.id].parts.push({
        size: size || '',
        value: spec.value,
      });
    }
    (item.sizeGroups || []).forEach(function (group) {
      (group.specs || []).forEach(function (line) {
        add(parseSpecLine(line), group.name);
      });
    });
    const extraLines = isLiftChair(item)
      ? item.specLines || []
      : (item.specLines || []).concat(item.dimensions || []).concat(item.details || []);
    const fallbackSize =
      (item.sizeGroups || []).length || !(item.sizes || []).length
        ? ''
        : uniqueLines(item.sizes).join(' / ');
    extraLines.forEach(function (line) {
      add(parseSpecLine(line), fallbackSize);
    });
    return map;
  }

  const CHAIR_SPEC_ORDER = [
    { id: 'max-weight', key: 'Maximum Weight Recommendation' },
    { id: 'item-dimensions', key: 'Item Dimensions D x W x H' },
    { id: 'fully-reclined', key: 'Fully Reclined Length' },
    { id: 'product-weight', key: 'Product Weight' },
    { id: 'seat-height', key: 'Seat Height' },
    { id: 'seat-depth', key: 'Seat Depth' },
    { id: 'seat-length', key: 'Seat Length' },
    { id: 'arm-height', key: 'Arm Height' },
  ];

  function buildSpecRows(items) {
    const maps = items.map(collectSpecMap);
    const order = [];
    const labels = {};
    const chairsOnly = items.length && items.every(isLiftChair);
    if (chairsOnly) {
      CHAIR_SPEC_ORDER.forEach(function (row) {
        if (
          maps.some(function (map) {
            return map[row.id];
          })
        ) {
          labels[row.id] = row.key;
          order.push(row.id);
        }
      });
    } else {
      maps.forEach(function (map) {
        Object.keys(map).forEach(function (id) {
          if (!labels[id]) {
            labels[id] = map[id].key;
            order.push(id);
          }
        });
      });
    }
    return order
      .map(function (id) {
        const cells = maps.map(function (map) {
          return specCellFromParts(map[id] && map[id].parts);
        });
        return finalizeSpecRow(labels[id], cells);
      })
      .filter(Boolean);
  }
  function collectKeyedValues(items, readEntries) {
    const order = [];
    const seen = {};
    items.forEach(function (item) {
      (readEntries(item) || []).forEach(function (entry) {
        if (!entry || !entry.id || seen[entry.id]) return;
        seen[entry.id] = entry;
        order.push(entry);
      });
    });
    return order.map(function (entry) {
      const values = items.map(function (item) {
        const match = (readEntries(item) || []).find(function (row) {
          return row.id === entry.id;
        });
        if (!match) return '';
        return match.value == null ? true : match.value;
      });
      return finalizeMatrixRow(entry.key, values);
    }).filter(Boolean);
  }

  function buildMatrix(items) {
    const chairTitles = liftChairFeatureTitles().filter(isSellingPointTitle);
    const useChairFeatures = chairTitles.length && items.some(isLiftChair);
    const featureRows = useChairFeatures
      ? collectKeyedValues(items, function (item) {
          if (!isLiftChair(item)) return [];
          const have = {};
          (item.highlights || []).forEach(function (line) {
            have[featureParts(line).id] = true;
          });
          return chairTitles
            .filter(function (line) {
              return have[featureParts(line).id];
            })
            .map(function (line) {
              const parts = featureParts(line);
              return { id: 'feat:' + parts.id, key: parts.key, value: true };
            });
        })
      : collectKeyedValues(items, function (item) {
          return (item.highlights || [])
            .filter(function (line) {
              return (
                isSellingPointTitle(line) &&
                !isLegacyFeatureCopy(line) &&
                !/pound weight limits|weight capacity|maximum weight recommendation/i.test(String(line || ''))
              );
            })
            .map(function (line) {
              const parts = featureParts(line);
              return { id: 'feat:' + parts.id, key: parts.key, value: parts.extra || true };
            });
        });

    const fabricRows = collectKeyedValues(items, function (item) {
      return (item.fabrics || []).map(function (fabric) {
        return { id: 'fabric:' + specId(fabric), key: fabric, value: true };
      });
    });

    const sizeRows = collectKeyedValues(items, function (item) {
      return (item.sizes || []).map(function (size) {
        return { id: 'size:' + specId(size), key: size, value: true };
      });
    });

    return {
      features: featureRows,
      fabrics: fabricRows,
      sizes: sizeRows,
      specs: buildSpecRows(items),
    };
  }

  function matrixCellHtml(value) {
    if (value === 'check') {
      return '<span class="tt-compare-modal__check" aria-label="Yes">✓</span>';
    }
    if (value === 'empty' || value == null || value === '') {
      return '<span class="tt-compare-modal__empty-cell">—</span>';
    }
    if (value && value.sized) {
      return (
        '<div class="tt-compare-modal__sized">' +
        value.sized
          .map(function (group) {
            const sizeLabel = (group.sizes || []).join(' / ');
            const display =
              group.value === 'HAS'
                ? '<span class="tt-compare-modal__empty-cell">—</span>'
                : '<span class="tt-compare-modal__sized-value">' + escapeHtml(group.value) + '</span>';
            return (
              '<div class="tt-compare-modal__sized-item">' +
              (sizeLabel
                ? '<span class="tt-compare-modal__sized-name">' + escapeHtml(sizeLabel) + '</span>'
                : '') +
              display +
              '</div>'
            );
          })
          .join('') +
        '</div>'
      );
    }
    return '<span class="tt-compare-modal__value">' + escapeHtml(String(value)) + '</span>';
  }

  function matrixRowHtml(row) {
    return (
      '<tr class="tt-compare-modal__row tt-compare-modal__row--matrix">' +
      '<th class="tt-compare-modal__label tt-compare-modal__label--matrix" scope="row">' +
      escapeHtml(row.label) +
      '</th>' +
      row.cells
        .map(function (value) {
          const kind = value === 'check' || value === 'empty' ? value : 'value';
          return (
            '<td class="tt-compare-modal__cell tt-compare-modal__cell--matrix tt-compare-modal__cell--' +
            kind +
            '">' +
            matrixCellHtml(value) +
            '</td>'
          );
        })
        .join('') +
      '</tr>'
    );
  }

  function matrixSectionHtml(title, rows) {
    if (!rows || !rows.length) return '';
    const colspan = (rows[0].cells || []).length + 1;
    return (
      '<tr class="tt-compare-modal__row tt-compare-modal__row--section"><th class="tt-compare-modal__section" colspan="' +
      colspan +
      '">' +
      escapeHtml(title) +
      '</th></tr>' +
      rows.map(matrixRowHtml).join('')
    );
  }

  function productColorSwatches(item) {
    return Array.isArray(item && item.colorSwatches) ? item.colorSwatches.filter(Boolean) : [];
  }

  function activeColorSwatch(item) {
    const swatches = productColorSwatches(item);
    if (!swatches.length) return null;
    const selectedName = String((item && item.selectedColor) || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    return (
      swatches.find(function (swatch) {
        return String(swatch.name || '').replace(/\s+/g, ' ').trim().toLowerCase() === selectedName;
      }) || swatches[0]
    );
  }

  function displayImage(item) {
    const swatch = activeColorSwatch(item);
    return (swatch && swatch.image) || (item && item.image) || '';
  }

  function displayUrl(item) {
    const swatch = activeColorSwatch(item);
    return (swatch && swatch.url) || (item && item.url) || '';
  }

  function colorSwatchStyle(swatch) {
    if (swatch && swatch.swatchImage) {
      return 'background-image: url(' + escapeAttr(swatch.swatchImage) + ');';
    }
    if (swatch && swatch.swatchColor) {
      return 'background-color: rgb(' + escapeAttr(swatch.swatchColor) + ');';
    }
    return '';
  }

  function colorCellsHtml(items) {
    return items
      .map(function (item) {
        const swatches = productColorSwatches(item);
        const active = activeColorSwatch(item);
        if (!swatches.length) {
          return '<td class="tt-compare-modal__cell tt-compare-modal__cell--swatches"><span class="tt-compare-modal__empty-cell">—</span></td>';
        }
        return (
          '<td class="tt-compare-modal__cell tt-compare-modal__cell--swatches">' +
          '<div class="tt-compare-modal__swatches" role="group" aria-label="Color">' +
          swatches
            .map(function (swatch) {
              const isActive = active && swatch.name === active.name;
              const style = colorSwatchStyle(swatch);
              return (
                '<button type="button" class="tt-compare-modal__swatch' +
                (isActive ? ' is-active' : '') +
                '" data-tt-compare-swatch data-product-id="' +
                escapeAttr(item.id) +
                '" data-color="' +
                escapeAttr(swatch.name || '') +
                '" data-image="' +
                escapeAttr(swatch.image || '') +
                '" data-url="' +
                escapeAttr(swatch.url || item.url || '') +
                '"' +
                (style ? ' style="' + style + '"' : '') +
                ' title="' +
                escapeAttr(swatch.name || '') +
                '" aria-label="Color: ' +
                escapeAttr(swatch.name || '') +
                '" aria-pressed="' +
                (isActive ? 'true' : 'false') +
                '"><span class="visually-hidden">' +
                escapeHtml(swatch.name || '') +
                '</span></button>'
              );
            })
            .join('') +
          '</div></td>'
        );
      })
      .join('');
  }

  function applyCompareSwatch(swatch) {
    const productId = String(swatch.getAttribute('data-product-id') || '');
    const color = swatch.getAttribute('data-color') || '';
    const image = swatch.getAttribute('data-image') || '';
    const url = swatch.getAttribute('data-url') || '';
    const item = selected.get(productId);
    if (item) {
      item.selectedColor = color;
      if (image) item.image = image;
      if (url) item.url = url;
      persist();
    }
    const cell = swatch.closest('td');
    if (cell) {
      cell.querySelectorAll('[data-tt-compare-swatch]').forEach(function (btn) {
        const isActive = btn === swatch;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
    if (!modal) return;
    const media = modal.querySelector('[data-tt-compare-media="' + productId + '"]');
    if (media) {
      if (url) media.setAttribute('href', url);
      const img = media.querySelector('img');
      if (img && image) {
        img.setAttribute('src', image);
        if (color) img.setAttribute('alt', (item && item.title ? item.title + ' — ' : '') + color);
      }
    }
    if (url) {
      modal.querySelectorAll('[data-tt-compare-link="' + productId + '"]').forEach(function (link) {
        link.setAttribute('href', url);
      });
    }
  }

  function renderModal() {
    if (!modalBody) return;
    const items = [...selected.values()].map(enrichProduct);
    if (items.length === 0) {
      modalBody.innerHTML = '<p class="tt-compare-modal__empty">Select products to compare.</p>';
      return;
    }

    const imageCells = items
      .map(function (item) {
        const image = displayImage(item);
        const url = displayUrl(item);
        return (
          '<td class="tt-compare-modal__cell tt-compare-modal__cell--media">' +
          '<div class="tt-compare-modal__media-row">' +
          '<a class="tt-compare-modal__media" href="' +
          escapeAttr(url) +
          '" data-tt-compare-media="' +
          escapeAttr(item.id) +
          '">' +
          (image
            ? '<img src="' +
              escapeAttr(image) +
              '" alt="' +
              escapeAttr(item.title) +
              '" width="320" height="320" loading="lazy">'
            : '') +
          '</a>' +
          (modal && modal.hasAttribute('data-tt-compare-lock')
            ? ''
            : '<div class="tt-compare-modal__media-side">' +
              '<button type="button" class="tt-compare-modal__remove" data-tt-compare-remove="' +
              escapeAttr(item.id) +
              '" aria-label="Remove ' +
              escapeAttr(item.title) +
              '">×</button>' +
              '</div>') +
          '</div></td>'
        );
      })
      .join('');

    const titleCells = items
      .map(function (item) {
        return (
          '<td class="tt-compare-modal__cell"><h3 class="tt-compare-modal__name"><a href="' +
          escapeAttr(displayUrl(item)) +
          '" data-tt-compare-link="' +
          escapeAttr(item.id) +
          '">' +
          escapeHtml(item.title) +
          '</a></h3></td>'
        );
      })
      .join('');

    const priceCells = items
      .map(function (item) {
        return (
          '<td class="tt-compare-modal__cell"><div class="tt-compare-modal__price-row">' +
          '<span class="tt-compare-modal__price">' +
          escapeHtml(item.price) +
          '</span>' +
          (item.comparePrice
            ? '<span class="tt-compare-modal__price tt-compare-modal__price--compare">' +
              escapeHtml(item.comparePrice) +
              '</span>'
            : '') +
          '</div></td>'
        );
      })
      .join('');

    const matrix = buildMatrix(items);

    const shopCells = items
      .map(function (item) {
        return (
          '<td class="tt-compare-modal__cell tt-compare-modal__cell--cta">' +
          '<a class="tt-compare-modal__shop" href="' +
          escapeAttr(displayUrl(item)) +
          '" data-tt-compare-link="' +
          escapeAttr(item.id) +
          '">Shop Now</a></td>'
        );
      })
      .join('');

    const productCols = items
      .map(function () {
        return '<col class="tt-compare-modal__col-product">';
      })
      .join('');

    function tableRow(label, cells, extraClass) {
      return (
        '<tr class="tt-compare-modal__row' +
        (extraClass ? ' ' + extraClass : '') +
        '"><th class="tt-compare-modal__label" scope="row">' +
        (label ? escapeHtml(label) : '&nbsp;') +
        '</th>' +
        cells +
        '</tr>'
      );
    }

    modalBody.innerHTML =
      '<table class="tt-compare-modal__table" style="--tt-compare-cols: ' +
      items.length +
      '"><colgroup><col class="tt-compare-modal__col-label">' +
      productCols +
      '</colgroup>' +
      tableRow('Product', imageCells, 'tt-compare-modal__row--media') +
      (items.some(function (item) {
        return productColorSwatches(item).length;
      })
        ? tableRow('Color', colorCellsHtml(items), 'tt-compare-modal__row--swatches')
        : '') +
      tableRow('Title', titleCells) +
      tableRow('Price', priceCells) +
      matrixSectionHtml('Features', matrix.features) +
      matrixSectionHtml('Fabric', matrix.fabrics) +
      matrixSectionHtml('Size', matrix.sizes) +
      matrixSectionHtml('Details', matrix.specs) +
      tableRow('', shopCells, 'tt-compare-modal__row--cta') +
      '</table>';
  }

  function openLimitNotice() {
    const limit = qs('[data-tt-compare-limit]');
    if (!limit) return;
    if (limit.parentElement !== document.body) {
      document.body.appendChild(limit);
    }
    limit.hidden = false;
    limit.classList.add('is-open');
    document.documentElement.classList.add('tt-compare-limit-open');
  }

  function openModal() {
    ensureEls();
    if (!modal || !dialog) return;
    if (selected.size < 2) return;
    lastFocus = document.activeElement;
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    renderModal();
    modal.hidden = false;
    modal.classList.add('is-open');
    document.documentElement.classList.add('tt-compare-modal-open');
    try {
      dialog.focus();
    } catch (e) {
      /* ignore */
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.hidden = true;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('tt-compare-modal-open');
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try {
        lastFocus.focus();
      } catch (e) {
        /* ignore */
      }
    }
  }

  function onClick(event) {
    const swatch = event.target.closest('[data-tt-compare-swatch]');
    if (swatch) {
      event.preventDefault();
      event.stopPropagation();
      applyCompareSwatch(swatch);
      return;
    }

    const toggle = event.target.closest('[data-tt-compare-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      toggleProduct(toggle);
      return;
    }

    const remove = event.target.closest('[data-tt-compare-remove]');
    if (remove) {
      event.preventDefault();
      event.stopPropagation();
      removeProduct(remove.getAttribute('data-tt-compare-remove'));
      return;
    }

    if (event.target.closest('[data-tt-compare-open]')) {
      event.preventDefault();
      event.stopPropagation();
      openModal();
      return;
    }

    if (event.target.closest('[data-tt-compare-clear]')) {
      event.preventDefault();
      event.stopPropagation();
      clearAll();
      return;
    }

    if (event.target.closest('[data-tt-compare-close]') || event.target === modal) {
      event.preventDefault();
      event.stopPropagation();
      closeModal();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
  }

  function ensureEls() {
    root = qs('collection-component.tt-collection-grid') || qs('.tt-collection-grid') || document;
    tray = qs('[data-tt-compare-tray]');
    slotsEl = qs('[data-tt-compare-slots]');
    openBtn = qs('[data-tt-compare-open]');
    countEl = qs('[data-tt-compare-count]');
    modal = qs('[data-tt-compare-modal]');
    modalBody = qs('[data-tt-compare-modal-body]');
    dialog = qs('[data-tt-compare-dialog]');
  }

  function bindFacetRefresh() {
    const grid = document.getElementById('ProductGridContainer');
    if (!grid || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(function () {
      window.requestAnimationFrame(syncButtons);
    });
    observer.observe(grid, { childList: true });
  }

  function init() {
    ensureEls();
    if (!tray || !modal) return false;

    if (!bound) {
      document.addEventListener('click', onClick, true);
      document.addEventListener('keydown', onKeydown);
      bound = true;
      bindFacetRefresh();
    }
    loadCatalog();
    restore();
    updateTray();
    document.documentElement.setAttribute('data-tt-compare-ready', 'true');
    return true;
  }

  function boot() {
    if (init()) return;
    let tries = 0;
    const timer = window.setInterval(function () {
      tries += 1;
      if (init() || tries > 20) window.clearInterval(timer);
    }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
